import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CallErrorCode } from 'matrix-js-sdk/lib/webrtc/call';
import { captureChatException } from 'shared/sentry';
import { MatrixContext } from './MatrixProvider';
import { ensureCallEventHandler, hasInProgressMatrixCall } from './shared/components/matrix/matrixUtils';
import CallEngine from './shared/components/matrix/call/CallEngine';
import CallManager from './shared/components/matrix/call/CallManager';

const getSupportedAudioConstraintKeys = () => {
  try {
    if (typeof navigator === 'undefined') return {};
    return navigator.mediaDevices?.getSupportedConstraints?.() ?? {};
  } catch (_) {
    return {};
  }
};

const pickSupportedAudioConstraintProfile = (profile, supported) => {
  return Object.entries(profile).reduce((acc, [key, value]) => {
    if (!supported[key]) return acc;
    acc[key] = value;
    return acc;
  }, {});
};

const logAudioConstraintDebug = (message, meta = {}) => {
  if (process.env.NODE_ENV === 'production') return;
  // eslint-disable-next-line no-console
  console.debug(`[matrix-call][audio-constraints] ${message}`, meta);
};

const SPEECH_AUDIO_CONSTRAINT_PROFILES = [
  // Prefer mono speech on supported browsers/devices.
  {
    name: 'speech_mono_isolation',
    constraints: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: { ideal: 1, max: 1 },
    },
  },
  // Fallback: conservative profile with the most widely supported keys.
  {
    name: 'speech_conservative',
    constraints: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  },
];

export const MatrixCallContext = createContext({
  incomingCall: null,
  activeCall: null,
  callError: null,
  lastCallEndedReason: null,
  lastCallHangupParty: null,
  placeVideoCall: async () => {},
  placeVoiceCall: async () => {},
  answerCall: async () => {},
  rejectCall: () => {},
  endCall: () => {},
  clearError: () => {},
  reportMediaError: () => {},
  clearLastCallEndState: () => {},
  resyncCallsFromSdk: () => {},
});

const MatrixCallProvider = ({ children }) => {
  const { client, isInitialized, isTimelineReady } = useContext(MatrixContext);
  const managerRef = useRef(null);
  /** Dedupe VoIP signalling per MatrixCall instance (avoids 429 on duplicate hangup/reject). */
  const hangupSentForCallRef = useRef(new WeakSet());
  const rejectSentForCallRef = useRef(new WeakSet());
  /** Stable call_id dedupe — matrix-js-sdk can race: hangup() before state becomes Ended may send m.call.hangup twice. */
  const hangupSentForCallIdRef = useRef(new Set());
  const rejectSentForCallIdRef = useRef(new Set());
  const optimizedTracksByCallIdRef = useRef(new Map());
  const lastSpeechOptimizedAtByCallIdRef = useRef(new Map());
  const supportedAudioConstraintKeysRef = useRef(null);
  const callStatsRef = useRef({
    started: 0,
    connected: 0,
    failedToConnect: 0,
    failedToConnectByCategory: {
      iceFailure: 0,
      permissionDenied: 0,
      timeout: 0,
      remoteReject: 0,
      signallingError: 0,
      userHangup: 0,
      unknown: 0,
    },
    audioConstraintProfiles: {
      attemptsByProfile: {},
      successByProfile: {},
      failuresByProfile: {},
      unsupportedProfilesSkipped: 0,
      totalConstraintErrors: 0,
      tracksOptimized: 0,
    },
  });

  const [state, setState] = useState({
    activeCall: null,
    incomingCall: null,
    callError: null,
    lastCallEndedReason: null,
    lastCallHangupParty: null,
  });

  const getFailureCategory = useCallback((endedReason, hangupParty) => {
    if (endedReason === CallErrorCode.IceFailed) return 'iceFailure';
    if (endedReason === CallErrorCode.NoUserMedia) return 'permissionDenied';
    if (endedReason === CallErrorCode.InviteTimeout) return 'timeout';
    if (endedReason === CallErrorCode.UserHangup) return 'userHangup';
    if (endedReason === CallErrorCode.SignallingFailed) {
      return hangupParty === 'remote' ? 'remoteReject' : 'signallingError';
    }
    return 'unknown';
  }, []);

  const optimizeSpeechAudioForCall = useCallback(async (call) => {
    if (!call || call.callHasEnded?.()) return;
    const callId = call?.callId;
    if (!callId || !call.localUsermediaStream) return;

    let trackSet = optimizedTracksByCallIdRef.current.get(callId);
    if (!trackSet) {
      trackSet = new Set();
      optimizedTracksByCallIdRef.current.set(callId, trackSet);
    }

    const lastAt = lastSpeechOptimizedAtByCallIdRef.current.get(callId) ?? 0;
    if (trackSet.size > 0 && Date.now() - lastAt < 2000) return;
    lastSpeechOptimizedAtByCallIdRef.current.set(callId, Date.now());

    const streams = [call.localUsermediaStream];
    const localFeeds = call.getLocalFeeds?.() ?? [];
    localFeeds.forEach((feed) => {
      if (feed?.stream && !streams.includes(feed.stream)) {
        streams.push(feed.stream);
      }
    });

    await Promise.all(
      streams.map(async (stream) => {
        const audioTracks = stream?.getAudioTracks?.() ?? [];
        await Promise.all(
          audioTracks.map(async (track) => {
            if (!track || trackSet.has(track)) return;
            if (track.readyState && track.readyState !== 'live') return;
            trackSet.add(track);
            try {
              track.contentHint = 'speech';
            } catch (_) {}
            if (typeof track.applyConstraints === 'function') {
              if (!supportedAudioConstraintKeysRef.current) {
                supportedAudioConstraintKeysRef.current = getSupportedAudioConstraintKeys();
              }
              const supportedConstraints = supportedAudioConstraintKeysRef.current ?? {};

              for (const profile of SPEECH_AUDIO_CONSTRAINT_PROFILES) {
                const profileName = profile.name;
                const audioConstraintStats = callStatsRef.current.audioConstraintProfiles;
                audioConstraintStats.attemptsByProfile[profileName] =
                  (audioConstraintStats.attemptsByProfile[profileName] ?? 0) + 1;
                try {
                  const supportedProfile = pickSupportedAudioConstraintProfile(
                    profile.constraints,
                    supportedConstraints
                  );
                  if (Object.keys(supportedProfile).length === 0) {
                    audioConstraintStats.unsupportedProfilesSkipped += 1;
                    continue;
                  }
                  await track.applyConstraints(supportedProfile);
                  audioConstraintStats.successByProfile[profileName] =
                    (audioConstraintStats.successByProfile[profileName] ?? 0) + 1;
                  audioConstraintStats.tracksOptimized += 1;
                  break;
                } catch (err) {
                  audioConstraintStats.failuresByProfile[profileName] =
                    (audioConstraintStats.failuresByProfile[profileName] ?? 0) + 1;
                  audioConstraintStats.totalConstraintErrors += 1;
                  logAudioConstraintDebug('applyConstraints failed; trying next profile', {
                    callId,
                    profile: profileName,
                    trackKind: track.kind,
                    errorName: err?.name,
                    errorMessage: err?.message,
                  });
                  // Try the next profile.
                }
              }
            }
          })
        );
      })
    );
  }, []);

  const prepareSdkCallTeardown = useCallback(() => {
    ensureCallEventHandler(client);
  }, [client]);

  const safeHangup = useCallback(
    (call, code = CallErrorCode.UserHangup, shouldEmit = false) => {
      if (!call || typeof call.hangup !== 'function') return;
      if (call.callHasEnded?.()) return;
      const callId = call.callId != null ? String(call.callId) : null;
      if (callId && hangupSentForCallIdRef.current.has(callId)) return;
      if (hangupSentForCallRef.current.has(call)) return;
      if (callId) hangupSentForCallIdRef.current.add(callId);
      hangupSentForCallRef.current.add(call);
      prepareSdkCallTeardown();
      try {
        call.hangup(code, shouldEmit);
      } catch (err) {
        const is429 =
          err?.httpStatus === 429 ||
          err?.statusCode === 429 ||
          err?.errcode === 'M_LIMIT_EXCEEDED';
        if (!is429) {
          captureChatException(err, { tags: { feature: 'matrix-call', action: 'hangup' } });
        }
      }
    },
    [prepareSdkCallTeardown]
  );

  const safeReject = useCallback(
    (call) => {
      if (!call || typeof call.reject !== 'function') return;
      const callId = call.callId != null ? String(call.callId) : null;
      if (callId && rejectSentForCallIdRef.current.has(callId)) return;
      if (rejectSentForCallRef.current.has(call)) return;
      if (callId) rejectSentForCallIdRef.current.add(callId);
      rejectSentForCallRef.current.add(call);
      prepareSdkCallTeardown();
      try {
        call.reject();
      } catch (err) {
        const is429 =
          err?.httpStatus === 429 ||
          err?.statusCode === 429 ||
          err?.errcode === 'M_LIMIT_EXCEEDED';
        if (!is429) {
          captureChatException(err, { tags: { feature: 'matrix-call', action: 'reject' } });
        }
      }
    },
    [prepareSdkCallTeardown]
  );

  useEffect(() => {
    if (!client || !isInitialized) return undefined;

    const engine = new CallEngine({ client, onUpdate: () => {}, maxCalls: 500 });
    const manager = new CallManager({
      client,
      engine,
      optimizeAudio: optimizeSpeechAudioForCall,
      safeHangup,
      safeReject,
      ensureCallEventHandler: prepareSdkCallTeardown,
      getFailureCategory,
      callStats: callStatsRef.current,
      timelineReady: isTimelineReady,
      onStateChange: (nextState) => {
        setState((prev) => ({ ...prev, ...nextState }));
      },
    });
    managerRef.current = manager;

    return () => {
      manager.destroy();
      engine.clearAll();
      managerRef.current = null;
      optimizedTracksByCallIdRef.current.clear();
      lastSpeechOptimizedAtByCallIdRef.current.clear();
      hangupSentForCallIdRef.current.clear();
      rejectSentForCallIdRef.current.clear();
    };
  }, [
    client,
    isInitialized,
    optimizeSpeechAudioForCall,
    safeHangup,
    safeReject,
    prepareSdkCallTeardown,
    getFailureCategory,
  ]);

  // After sync/timeline is ready (or recovers), re-scan SDK live calls — covers slow first /sync
  // or invites processed before Call.incoming was subscribed.
  useEffect(() => {
    if (!client || !isInitialized || !isTimelineReady) return undefined;
    managerRef.current?.retryHydrateAfterTimelineReady?.();
  }, [client, isInitialized, isTimelineReady]);

  const placeVideoCall = useCallback((roomId) => managerRef.current?.placeCall(roomId, 'video'), []);
  const placeVoiceCall = useCallback((roomId) => managerRef.current?.placeCall(roomId, 'voice'), []);
  const answerCall = useCallback(() => managerRef.current?.answerCall(), []);
  const rejectCall = useCallback(() => managerRef.current?.rejectCall(), []);
  const endCall = useCallback((endedReason) => managerRef.current?.endCall(endedReason), []);
  const clearError = useCallback(() => managerRef.current?.clearError(), []);
  const reportMediaError = useCallback(
    (error) => managerRef.current?.reportMediaError?.(error),
    []
  );
  const clearLastCallEndState = useCallback(
    () => managerRef.current?.clearLastCallEndState?.(),
    []
  );
  const resyncCallsFromSdk = useCallback(
    () => managerRef.current?.retryHydrateAfterTimelineReady?.(),
    []
  );

  /** Prompt before tab close/reload while a call is active (browsers show a generic message). */
  useEffect(() => {
    const inCallUi = Boolean(state.activeCall || state.incomingCall);
    const inCallSdk = Boolean(client && hasInProgressMatrixCall(client));
    if (!inCallUi && !inCallSdk) return undefined;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.activeCall, state.incomingCall, client]);

  const value = useMemo(
    () => ({
      incomingCall: state.incomingCall,
      activeCall: state.activeCall,
      callError: state.callError,
      lastCallEndedReason: state.lastCallEndedReason,
      lastCallHangupParty: state.lastCallHangupParty,
      placeVideoCall,
      placeVoiceCall,
      answerCall,
      rejectCall,
      endCall,
      clearError,
      reportMediaError,
      clearLastCallEndState,
      resyncCallsFromSdk,
    }),
    [
      state,
      placeVideoCall,
      placeVoiceCall,
      answerCall,
      rejectCall,
      endCall,
      clearError,
      reportMediaError,
      clearLastCallEndState,
      resyncCallsFromSdk,
    ]
  );

  return <MatrixCallContext.Provider value={value}>{children}</MatrixCallContext.Provider>;
};

export default MatrixCallProvider;
